import * as React from 'react';
import * as SelectPrimitive from '@radix-ui/react-select';
import { cn } from '@/lib/utils';
import { ChevronDown, CheckIcon } from '../icons';

/**
 * m16 Select — the design-system dropdown for styled UI. Never use a native
 * `<select>` for styled controls: the OS owns the popup chrome (e.g. the dark
 * macOS panel) and it can't be themed to the bone/teal palette.
 *
 * Built on Radix Select so it is robust where a hand-rolled popover is not:
 * it PORTALS the option list to `document.body`, so it is never clipped by an
 * `overflow-y-auto` container (contact modal, filter bottom-sheet) and always
 * stacks above them; and it coordinates focus/dismiss with Radix Dialog (the
 * bottom sheet) out of the box. Full keyboard a11y (open/close, ↑/↓, Home/End,
 * type-ahead, Esc, focus-return) and `role="listbox"`/`role="option"` come from
 * Radix.
 *
 * Prefer the high-level {@link Select} (native-select-shaped API) for forms and
 * sheets. The lower-level compound parts are exported for bespoke triggers
 * (e.g. the catalog chip {@link Dropdown}, which layers its own trigger on top).
 *
 *   <Select
 *     value={value}
 *     onChange={setValue}
 *     options={[{ value: 'a', label: 'Alpha' }]}
 *     aria-label="Example"
 *   />
 */

const SelectRoot = SelectPrimitive.Root;
const SelectGroup = SelectPrimitive.Group;
const SelectValue = SelectPrimitive.Value;

const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Trigger
    ref={ref}
    className={cn(
      'flex h-11 w-full items-center justify-between gap-2 rounded-md border border-edge bg-bone-surface px-3 text-[16px] text-ink outline-none transition-colors',
      'data-[state=open]:border-brand focus-visible:border-brand focus-visible:shadow-[0_0_0_3px_rgb(var(--brand)/0.1)]',
      'data-[placeholder]:text-ink-muted',
      'disabled:cursor-not-allowed disabled:opacity-60',
      '[&>span]:min-w-0 [&>span]:truncate',
      className,
    )}
    {...props}
  >
    {children}
    <SelectPrimitive.Icon asChild>
      <ChevronDown className="h-4 w-4 shrink-0 text-ink-muted transition-transform" />
    </SelectPrimitive.Icon>
  </SelectPrimitive.Trigger>
));
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

const SelectScrollUpButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollUpButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollUpButton
    ref={ref}
    className={cn('flex cursor-default items-center justify-center py-1 text-ink-muted', className)}
    {...props}
  >
    <ChevronDown className="h-3.5 w-3.5 rotate-180" />
  </SelectPrimitive.ScrollUpButton>
));
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName;

const SelectScrollDownButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollDownButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollDownButton
    ref={ref}
    className={cn('flex cursor-default items-center justify-center py-1 text-ink-muted', className)}
    {...props}
  >
    <ChevronDown className="h-3.5 w-3.5" />
  </SelectPrimitive.ScrollDownButton>
));
SelectScrollDownButton.displayName = SelectPrimitive.ScrollDownButton.displayName;

const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({ className, children, position = 'popper', ...props }, ref) => (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      ref={ref}
      position={position}
      // z-[60] clears the modal/bottom-sheet layer (z-50). Height is capped to
      // the space Radix measured, so long lists scroll instead of overflowing.
      className={cn(
        'relative z-[60] max-h-[min(20rem,var(--radix-select-content-available-height))] overflow-hidden rounded-lg border border-edge bg-bone-surface p-1 shadow-m-md',
        'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
        position === 'popper' &&
          'data-[side=bottom]:translate-y-1 data-[side=top]:-translate-y-1 min-w-[var(--radix-select-trigger-width)]',
        className,
      )}
      {...props}
    >
      <SelectScrollUpButton />
      <SelectPrimitive.Viewport
        className={cn(
          position === 'popper' &&
            'h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]',
        )}
      >
        {children}
      </SelectPrimitive.Viewport>
      <SelectScrollDownButton />
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
));
SelectContent.displayName = SelectPrimitive.Content.displayName;

const SelectLabel = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label
    ref={ref}
    className={cn(
      'px-2.5 py-1.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-muted',
      className,
    )}
    {...props}
  />
));
SelectLabel.displayName = SelectPrimitive.Label.displayName;

const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn(
      // min-h-10 keeps a comfortable ≥40px tap target on mobile.
      'relative flex min-h-10 w-full cursor-pointer select-none items-center rounded-md py-2 pl-8 pr-3 text-[14px] text-ink-soft outline-none',
      'data-[highlighted]:bg-brand-dim data-[highlighted]:text-brand',
      'data-[state=checked]:font-semibold data-[state=checked]:text-brand',
      'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
      className,
    )}
    {...props}
  >
    <span className="absolute left-2.5 flex h-3.5 w-3.5 items-center justify-center text-brand">
      <SelectPrimitive.ItemIndicator>
        <CheckIcon className="h-3.5 w-3.5" />
      </SelectPrimitive.ItemIndicator>
    </span>
    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
  </SelectPrimitive.Item>
));
SelectItem.displayName = SelectPrimitive.Item.displayName;

const SelectSeparator = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator
    ref={ref}
    className={cn('mx-1 my-1 h-px bg-edge', className)}
    {...props}
  />
));
SelectSeparator.displayName = SelectPrimitive.Separator.displayName;

// ── High-level Select ──────────────────────────────────────────────────────
// Radix reserves the empty string for "no value / show placeholder", so a
// `''` option (e.g. the catalog "All region" / "Active" defaults) is round-
// tripped through a sentinel and mapped back on change — callers keep using
// `''` exactly like a native <select>.
const EMPTY_VALUE = '__empty__';
const toRadix = (v: string) => (v === '' ? EMPTY_VALUE : v);
const fromRadix = (v: string) => (v === EMPTY_VALUE ? '' : v);

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps {
  options: SelectOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  /** id on the trigger (associate with an external <label htmlFor>). */
  id?: string;
  'aria-label'?: string;
  'aria-labelledby'?: string;
  /** Extra classes on the trigger button. */
  className?: string;
  /** Extra classes on the popover. */
  contentClassName?: string;
  /** Popover edge alignment relative to the trigger. */
  align?: 'start' | 'center' | 'end';
}

/**
 * Native-`<select>`-shaped API on top of the m16 Radix Select. Drop-in for the
 * value/options/onChange pattern used across the app.
 */
export function Select({
  options,
  value,
  onChange,
  placeholder,
  disabled,
  id,
  className,
  contentClassName,
  align = 'start',
  ...aria
}: SelectProps) {
  return (
    <SelectRoot
      value={toRadix(value)}
      onValueChange={(v) => onChange(fromRadix(v))}
      disabled={disabled}
    >
      <SelectTrigger
        id={id}
        aria-label={aria['aria-label']}
        aria-labelledby={aria['aria-labelledby']}
        className={className}
      >
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent align={align} className={contentClassName}>
        {options.map((o) => (
          <SelectItem key={o.value} value={toRadix(o.value)} disabled={o.disabled}>
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </SelectRoot>
  );
}

export {
  SelectRoot,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator,
  SelectScrollUpButton,
  SelectScrollDownButton,
};
